<template>
  <v-dialog
    :model-value="modelValue"
    max-width="723"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="delete-dialog-card">
      <div class="delete-dialog-content">
        <div class="delete-dialog-header">
          <span class="delete-dialog-title">Delete Chat</span>
          <v-btn
            icon
            size="40"
            variant="text"
            class="delete-dialog-close"
            aria-label="Close dialog"
            @click="$emit('update:modelValue', false)"
          >
            <v-icon size="40" aria-hidden="true"
              >mdi-close-circle-outline</v-icon
            >
          </v-btn>
        </div>

        <div class="delete-dialog-body">
          <div class="delete-dialog-text-group">
            <p class="delete-dialog-subtitle">
              Are you sure you want to delete any memories saved during this
              chat:
            </p>
            <p class="delete-dialog-conversation-title">
              "{{ conversation?.title }}"
            </p>
          </div>
          <div class="delete-dialog-actions">
            <v-btn
              class="delete-dialog-cancel-btn"
              variant="outlined"
              @click="$emit('update:modelValue', false)"
            >
              Cancel
            </v-btn>
            <v-btn
              class="delete-dialog-confirm-btn"
              :loading="isDeleting"
              @click="confirmDelete"
            >
              Delete
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { del as idbDel } from 'idb-keyval';

import { type Conversation } from '@/services/conversationService';
import { useConversationStore } from '@/stores/conversationStore';
import { useMessageStore } from '@/stores/messageStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { CACHE_KEYS } from '@/utils/cache';
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps<{
  modelValue: boolean;
  conversation: Conversation | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  deleted: [];
}>();

const route = useRoute();
const router = useRouter();
const conversationStore = useConversationStore();
const messageStore = useMessageStore();
const notificationStore = useNotificationStore();

const isDeleting = ref(false);

const confirmDelete = async () => {
  if (!props.conversation) return;

  try {
    isDeleting.value = true;
    await conversationStore.deleteConversationById(props.conversation.id);
    idbDel(CACHE_KEYS.CONVERSATION_METADATA(props.conversation.id));
    messageStore.invalidateConversationMessages(props.conversation.id);
    if (route.params.id === props.conversation.id) {
      router.push('/en/echo');
    }
    emit('update:modelValue', false);
    emit('deleted');
  } catch (error) {
    console.error('Failed to delete conversation:', error);
    notificationStore.notify(
      'Failed to delete conversation. Please try again.',
    );
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped lang="scss">
.delete-dialog-card {
  padding: 32px;
  border-radius: 24px !important;
  background-color: #fff;
}

.delete-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
}

.delete-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.delete-dialog-title {
  color: #303030;
  font-family: Poppins;
  font-size: 24px;
  font-weight: 600;
  line-height: normal;
}

.delete-dialog-close {
  color: #959595 !important;
}

.delete-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 48px;
  align-items: flex-end;
}

.delete-dialog-text-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.delete-dialog-subtitle {
  color: #303030;
  font-family: Poppins;
  font-size: 20px;
  font-weight: 600;
  line-height: 150%;
  margin: 0;
}

.delete-dialog-conversation-title {
  color: #1e1e1e;
  font-family: Poppins;
  font-size: 24px;
  font-weight: 600;
  line-height: 150%;
  margin: 0;
}

.delete-dialog-actions {
  display: flex;
  gap: 12px;
  width: 100%;
}

@media only screen and (width < $breakpoint-mobile) {
  .delete-dialog-actions {
    flex-direction: column;
  }
}

::v-deep(.delete-dialog-cancel-btn.v-btn) {
  flex: 1;
  min-height: 52px;
  border-radius: 8px;
  border-color: #959595;
  background-color: #fff;
  text-transform: none;

  .v-btn__content {
    color: #303030;
    font-family: Poppins;
    font-size: 16px;
    font-weight: 500;
  }
}

::v-deep(.delete-dialog-confirm-btn.v-btn) {
  flex: 1;
  min-height: 52px;
  border-radius: 8px;
  background-color: #f0252b;
  text-transform: none;

  .v-btn__content {
    color: #fff;
    font-family: Poppins;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: normal;
  }
}
</style>
